import {
  CampaignMedium,
  MarketingBusinesses,
  MarketingCampaigns,
  MarketingMediumTemplates,
  ServiceCategory,
} from '@prisma/client';
import { format, subDays } from 'date-fns';
import {
  CampaignFilter,
  MarketingCampaignSegmentWithFilters,
  PocketflowsEmailTemplate,
  PocketflowsSmsTemplate,
  CampaignSegment,
  MarketingCampaignWithSource,
  SendAt,
  SendNow,
  PocketflowsProjectEmailTemplate,
  PocketflowsBusinessSegmentUsage,
  MarketingBusinessesWithCampaigns,
  SegmentCustomer,
  PocketflowsCustomer,
} from '../../types/marketing';
import {
  formatDollarAmount,
  isNil,
  pluralize,
} from '../../utils/global/helper';
import {
  CREDITS_COST_CENTS,
  POCKETFLOWS_API_URL,
  PREBUILT_EXCLUDE_SEGMENTS_DESCRIPTION,
  PREBUILT_INCLUDE_SEGMENTS_CONTENT,
  SMS_CREDITS_PER_MMS,
} from '../../constants/marketing';
import { LoadedUser } from '../../interfaces';

export function getCampaignMediumsText(
  mediums: CampaignMedium[],
  capitalize = false,
) {
  const medium = mediums
    .map(med => {
      if (med === 'EMAIL') return 'Email';
      return med;
    })
    .join('& ');
  const campaignText = capitalize ? 'Campaign' : 'campaign';
  return `${medium} ${campaignText}`;
}

export function getCampaignLastEditedText(campaign: MarketingCampaigns) {
  const isEdited = new Date(campaign.updatedAt) > new Date(campaign.createdAt);
  if (!isEdited) return '';
  return `Last edited ${format(
    new Date(campaign.updatedAt),
    "EEE MMMM d, yyyy 'at' h:mmaaa",
  )}`;
}

function isDateValid(date: Date) {
  return !Number.isNaN(date.getTime());
}

export function validateFilters(
  filters: CampaignFilter[],
  marketingBusiness: Pick<MarketingBusinesses, 'businessId'>,
) {
  if (!filters?.length) {
    throw new Error('Please add at least one filter');
  }
  return filters.map(filter => {
    const { category, value, operator } = filter;
    if (!category || isNil(value) || !operator)
      throw new Error('Invalid filter');

    let transformedValue = value;

    switch (category) {
      case 'LAST_APPOINTMENT_WITHIN_DAYS':
      case 'APPOINTMENT_COUNT': {
        const numValue = Number(value);
        if (!Number.isInteger(numValue) || numValue < 0) {
          throw new Error('Invalid value for APPOINTMENT_COUNT');
        }
        if (category === 'LAST_APPOINTMENT_WITHIN_DAYS') {
          const date = subDays(new Date(), numValue);
          if (!isDateValid(date)) {
            throw new Error('Invalid value for LAST_APPOINTMENT_WITHIN_DAYS');
          }
        }

        transformedValue = numValue;
        break;
      }
      case 'LAST_APPOINTMENT_DATE':
      case 'APPOINTMENT_CREATED':
      case 'NEXT_APPOINTMENT_DATE': {
        // confirm that value, a string in yyyy-MM-dd format, is a valid date
        if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
          throw new Error('Invalid value for APPOINTMENT_CREATED');
        }

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
          throw new Error('Invalid value for APPOINTMENT_CREATED');
        }

        transformedValue = value;
        break;
      }
      case 'SPEND': {
        const dollarString = value.toString();
        // allow numbers like 100 or 100.00 or 100.1 but not 100.123
        if (!/^\d+(\.\d{1,2})?$/.test(dollarString)) {
          throw new Error('Invalid value for SPEND');
        }
        const num = parseFloat(dollarString);
        if (Number.isNaN(num) || num < 0) {
          throw new Error('Invalid value for SPEND');
        }
        transformedValue = num;
        break;
      }
      case 'EMPLOYEE_RELATION': {
        if (!marketingBusiness.businessId) {
          throw new Error('Filter not available for this business');
        }
        if (typeof value !== 'string' || !value?.trim?.()) {
          throw new Error('Invalid value for employee');
        }
        transformedValue = value;
        break;
      }
      case 'APPOINTMENT_SERVICE': {
        const options = Object.values(ServiceCategory);
        if (!options.includes(value as ServiceCategory)) {
          throw new Error('Invalid value for appointment service');
        }
        transformedValue = value as ServiceCategory;
        break;
      }
      default:
        throw new Error('Invalid filter category');
        break;
    }

    return {
      category,
      value: transformedValue,
      operator,
    } as CampaignFilter;
  });
}

export function getFilterText({
  filter,
  employees,
}: {
  filter: CampaignFilter;
  employees: LoadedUser['business']['employees'];
}) {
  const { category, value, operator } = filter;
  switch (category) {
    case 'LAST_APPOINTMENT_WITHIN_DAYS':
      return `Last appointment visit ${
        operator === 'GT' ? 'is within' : 'is not within'
      } the last ${value} days`;
    case 'NEXT_APPOINTMENT_DATE':
    case 'LAST_APPOINTMENT_DATE':
      return `${
        category === 'LAST_APPOINTMENT_DATE' ? 'Last' : 'Next'
      } appointment visit ${
        operator === 'GT' ? 'is after' : 'is before'
      } ${format(new Date(`${value}T00:00:00.000`), 'MMMM d, yyyy')}`;
    case 'APPOINTMENT_COUNT':
      return `Number of visits ${
        operator === 'GT' ? 'is greater than' : 'is less than'
      } ${value} ${pluralize('visit', parseInt(String(value), 10))}`;
    case 'SPEND':
      return `Total spend ${
        operator === 'GT' ? 'is greater' : 'is less'
      } than ${formatDollarAmount(value, true)}`;
    case 'APPOINTMENT_CREATED':
      return `Appointment created ${
        operator === 'GT' ? 'is after' : 'is before'
      } ${format(new Date(`${value}T00:00:00.000`), 'MMMM d, yyyy')}`;
    case 'EMPLOYEE_RELATION': {
      const employee = employees?.find(e => e.employeeId === value);
      return `Is on the mailing list for ${
        (employee ? employee?.displayName : '') || 'Unknown'
      }`;
    }
    case 'APPOINTMENT_SERVICE': {
      const service = Object.values(ServiceCategory).find(s => s === value);
      return `Is a client for the ${
        service ? service.toLowerCase() : 'unknown'
      } service`;
    }
    default:
      return '';
  }
}

export function getCampaignPreviewUrl(campaign: MarketingCampaignWithSource) {
  const url = new URL(
    `${POCKETFLOWS_API_URL}/campaigns/${campaign.sourceCampaignId}/preview.png`,
  );
  url.searchParams.set(
    'bust',
    new Date(campaign.updatedAt).getTime().toString(),
  );
  return url.toString();
}

export function getEmailTemplatePreviewUrl(template: MarketingMediumTemplates) {
  const url = new URL(
    `${POCKETFLOWS_API_URL}/email_templates/${template.sourceTemplateId}/preview.png`,
  );
  url.searchParams.set(
    'bust',
    new Date(template.updatedAt).getTime().toString(),
  );
  return url.toString();
}

export function getProjectEmailTemplatePreviewUrl(
  template: PocketflowsProjectEmailTemplate,
) {
  const url = new URL(
    `${POCKETFLOWS_API_URL}/project_email_templates/${template.id}/preview.png`,
  );
  return url.toString();
}

export function getSmsTemplatePreviewUrl(template: MarketingMediumTemplates) {
  const url = new URL(
    `${POCKETFLOWS_API_URL}/sms_templates/${template.sourceTemplateId}/preview.png`,
  );
  url.searchParams.set(
    'bust',
    new Date(template.updatedAt).getTime().toString(),
  );
  return url.toString();
}

export function getEmbedUrl(urlString: string) {
  const url = new URL(urlString);
  const seatchParams = new URLSearchParams(url.search);
  seatchParams.set('hide_save_button', 'true');
  url.search = seatchParams.toString();
  return url.toString();
}

export function getSendAtText(sendTime: SendNow | SendAt) {
  return sendTime?.type === 'NOW'
    ? 'Send now'
    : sendTime?.type === 'SCHEDULED' && sendTime.dateTime
    ? `Send at ${format(
        new Date(sendTime?.dateTime),
        "h:mma 'on' EEEE, MMM d",
      )}`
    : 'Send at -';
}

export function isSmsTemplateValid(sourceTemplate: PocketflowsSmsTemplate) {
  return (
    !!sourceTemplate.name.trim().length &&
    !!sourceTemplate.message.trim().length
  );
}

export function isEmailTemplateValid(sourceTemplate: PocketflowsEmailTemplate) {
  return (
    !!sourceTemplate.name?.trim().length &&
    !!sourceTemplate.subject?.trim().length &&
    (!!sourceTemplate.body?.trim().length ||
      !!sourceTemplate.attachments?.length ||
      !!sourceTemplate.template_json?.doc?.content?.length ||
      !!sourceTemplate.new_template_json?.body?.rows?.length)
  );
}

export function transformSegment(
  segment: MarketingCampaignSegmentWithFilters | undefined,
) {
  if (!segment) return null;
  return {
    name: segment.name,
    condition: segment.condition,
    filters: segment.filters as CampaignFilter[],
    id: segment.id,
    topPercentage: segment.topPercentage ?? undefined,
  };
}

export function getSegmentsDescription(
  includeSegment: CampaignSegment | null,
  excludeSegment: CampaignSegment | null,
) {
  const defaultIncludeLabel =
    PREBUILT_INCLUDE_SEGMENTS_CONTENT.ALL_CUSTOMERS.label;
  const defaultExcludeLabel =
    PREBUILT_EXCLUDE_SEGMENTS_DESCRIPTION.NO_CUSTOMERS;

  const getText = (
    segment: typeof includeSegment,
    type: 'include' | 'exclude',
  ) => {
    const defaultLabel =
      type === 'include' ? defaultIncludeLabel : defaultExcludeLabel;
    if (!segment) return defaultLabel;
    if ('id' in segment && typeof segment.id === 'string') {
      const content =
        type === 'include'
          ? PREBUILT_INCLUDE_SEGMENTS_CONTENT[
              segment.id as keyof typeof PREBUILT_INCLUDE_SEGMENTS_CONTENT
            ].label
          : PREBUILT_EXCLUDE_SEGMENTS_DESCRIPTION[
              segment.id as keyof typeof PREBUILT_EXCLUDE_SEGMENTS_DESCRIPTION
            ];
      return content || defaultLabel;
    }

    if (!segment.filters || !segment.filters.length) return defaultLabel;

    return segment.name || 'Custom segment';
  };

  const includeSegmentText = getText(includeSegment, 'include');
  const excludeSegmentText = getText(excludeSegment, 'exclude');

  return `${includeSegmentText || defaultIncludeLabel}${
    excludeSegmentText ? ` - excluding ${excludeSegmentText}.` : ''
  }`;
}

export function calculateCreditsCost({
  medium,
  segments,
}: {
  medium: CampaignMedium;
  segments: number;
}) {
  const segmentCost = segments * CREDITS_COST_CENTS[medium];
  return segmentCost;
}

export function campaignHasEmailStatistics(
  campaign: MarketingCampaignWithSource,
) {
  return (
    campaign.mediums.some(med => med === 'EMAIL') &&
    !!campaign.source.statistics.emails?.sent
  );
}

export function calculateCreditsUsage({
  marketingData,
  segmentUsage,
}: {
  marketingData: MarketingBusinessesWithCampaigns | undefined | null;
  segmentUsage: PocketflowsBusinessSegmentUsage | undefined | null;
}) {
  const emailSegmentUsage =
    (segmentUsage?.email ?? 0) +
    (segmentUsage?.scheduled_segment_usage?.email ?? 0);
  const rawSmsSegmentUsage =
    (segmentUsage?.sms ?? 0) +
    (segmentUsage?.scheduled_segment_usage?.sms ?? 0);
  const rawMmsSegmentUsage =
    (segmentUsage?.mms ?? 0) +
    (segmentUsage?.scheduled_segment_usage?.mms ?? 0);
  const smsSegmentUsage =
    rawSmsSegmentUsage + rawMmsSegmentUsage * SMS_CREDITS_PER_MMS;
  const smsSurplus = marketingData?.smsCreditsSurplus ?? 0;
  const emailSurplus = marketingData?.emailCreditsSurplus ?? 0;
  const remainingSmsCredits = Math.max(
    0,
    (marketingData?.monthlySmsCredits || 0) + smsSurplus - smsSegmentUsage,
  );
  const remainingEmailCredits = Math.max(
    0,
    (marketingData?.monthlyEmailCredits || 0) +
      emailSurplus -
      emailSegmentUsage,
  );

  return {
    surplus: {
      sms: smsSurplus,
      email: emailSurplus,
    },
    remaining: {
      sms: remainingSmsCredits,
      email: remainingEmailCredits,
    },
  };
}

export function getTemplateEstimatedSegments(
  customersCount: number,
  template: PocketflowsSmsTemplate | PocketflowsEmailTemplate,
) {
  if ('estimated_segments' in template) {
    const costPerCustomer =
      (template.estimated_segments?.sms ?? 0) +
      (template.estimated_segments?.mms ?? 0) * SMS_CREDITS_PER_MMS;
    return Math.ceil(customersCount * costPerCustomer);
  }
  return customersCount;
}

export function dedupeCustomers({
  sourceCustomers,
  customers,
}: {
  sourceCustomers: PocketflowsCustomer[];
  customers: SegmentCustomer[];
}) {
  const seenEmails = new Set<string>();
  const seenPhoneNumbers = new Set<string>();

  const dedupedSourceCustomers = sourceCustomers.filter(customer => {
    if (customer.email_address && seenEmails.has(customer.email_address)) {
      return false;
    }
    if (customer.phone_number && seenPhoneNumbers.has(customer.phone_number)) {
      return false;
    }
    if (customer.email_address) seenEmails.add(customer.email_address);
    if (customer.phone_number) seenPhoneNumbers.add(customer.phone_number);
    return true;
  });

  const dedupedCustomers = customers.filter(customer => {
    if (customer.user.email && seenEmails.has(customer.user.email)) {
      return false;
    }
    if (
      customer.user.phoneNumber &&
      seenPhoneNumbers.has(customer.user.phoneNumber)
    ) {
      return false;
    }
    if (customer.user.email) seenEmails.add(customer.user.email);
    if (customer.user.phoneNumber)
      seenPhoneNumbers.add(customer.user.phoneNumber);

    return true;
  });

  return {
    sourceCustomers: dedupedSourceCustomers,
    customers: dedupedCustomers,
  };
}
